import React from "react";
import U2000BasicTableList from "../basic/U2000BasicTableList";
import type { U2000GridStateHandlers } from "../../../reducer/formReducer/U2000GridStateHandlers";
import U2000CommonTableFooter from "../basic/U2000CommonTableFooter";

// Types génériques pour les colonnes et les données
export interface DetailTableColumn {
  name: string;
  label: string;
  align?: "left" | "center" | "right";
  widthClass?: string;
  headerDisplay?: (column: DetailTableColumn, label: string) => React.ReactNode;
  [key: string]: any;
}

export interface DetailTableRow {
  id: string | number;
  [key: string]: any;
}

export interface U2000DetailTableProps<T extends Record<string, any>> {
  gridStateHandlers: U2000GridStateHandlers<T>;
  rowClickFn?: (row: DetailTableRow) => void;
  allowRowSelection?: boolean;
  label?: string;
  /** Ligne(s) `<tr>` rendues dans un `<tfoot>` (voir `U2000BasicTableList`). */
  tableFooter?: React.ReactNode;
  /** Voir `U2000BasicTableList.enableHorizontalScroll`. */
  enableHorizontalScroll?: boolean;
}

/**
 * Met à jour une ligne spécifique dans la table (ex: après modification d'une case à cocher)
 * Notifie le parent si la sélection change
 * @param row Ligne modifiée (doit contenir id)
 */
function U2000DetailTable<T extends Record<string, any>>({
  gridStateHandlers,
  rowClickFn,
  allowRowSelection = false,
  label,
  tableFooter,
  enableHorizontalScroll = false,
}: U2000DetailTableProps<T>) {
  const rowUpdated = (row: T & { id?: any; idChecked?: boolean }) => {
    if (row && allowRowSelection) {
      gridStateHandlers.selectRow(row.id, row.idChecked as boolean);
    }
  };

  /**
   * Sélectionne ou désélectionne toutes les lignes de la table
   * Met à jour l'état local et notifie le parent via onRowSelectedChange
   * @param isSelected true pour tout sélectionner, false pour tout désélectionner
   */
  function handleSelectAllRow(isSelected: boolean) {
    if (allowRowSelection) {
      gridStateHandlers.selectAllRow(isSelected);
    }
  }

  return (
    <>
      <U2000BasicTableList
        gridStateHandlers={gridStateHandlers}
        rowClickFn={rowClickFn}
        rowUpdated={rowUpdated}
        handleSelectAllRow={handleSelectAllRow}
        allowRowSelection={allowRowSelection}
        tableFooter={tableFooter}
        enableHorizontalScroll={enableHorizontalScroll}
      />
      {/* Pied de page avec nombre d'éléments affichés */}
      <U2000CommonTableFooter nbItem={gridStateHandlers.getTotalRows()} label={label ?? ""} />
    </>
  );
}

export default U2000DetailTable;
